import { Controller } from "@hotwired/stimulus"

// Wraps the videos search form and, on mobile, the full-screen filter modal.
//
// While the modal is open, filter changes update results first, then request
// predictive facet counts independently.
// The query does not: it searches on Enter, or on "Show results" if the box
// still has uncommitted text. "Cancel" discards that and returns to
// where you were when you opened the modal.
//
// Rather than navigate back to a cached URL on cancel — a full server round-trip
// that feels janky next to the live in-modal updates — we snapshot the committed
// state when the modal opens and restore it from the DOM instantly on cancel. No
// network, no flash.
export default class extends Controller {
  static targets = [ 'wrapper', 'loadingQuery' ];

  connect(){
    this.element.modal = this;
    this.boundPrepareForCache = this.prepareForCache.bind(this);
    document.addEventListener('turbo:before-cache', this.boundPrepareForCache);

    // Arriving from the global header search icon on another page: the link
    // carries ?open_search=1 so we open the modal on load. Strip the flag
    // from the URL first so a refresh or Cancel-restore doesn't reopen it.
    const params = new URLSearchParams(window.location.search);
    if (params.get('open_search')) {
      params.delete('open_search');
      const query = params.toString();
      const cleaned = window.location.pathname + (query ? `?${query}` : '');
      window.history.replaceState(window.history.state, '', cleaned);
      window.rafaRememberVisit?.();
      this.openModal();
    }
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.boundPrepareForCache);
    window.cancelAnimationFrame(this.entranceFrame);
  }

  open(e) {
    e.preventDefault();
    this.openModal();
  }

  openModal() {
    // Always open on the search screen (Screen A), even if the last session
    // committed from the filters screen. Done before snapshotting so the
    // captured HTML — and therefore cancel — also lands on Screen A.
    const screens = this.wrapperTarget.querySelector('[data-controller~="modal-screens"]');
    if (screens) screens.dataset.screen = 'search';
    // Capture the committed state before the user starts exploring. These are
    // exactly the regions the index.turbo_stream response replaces, plus the
    // query value (which the stream never re-renders) and the current URL.
    // The cold entry modal (in the layout, off the results page) has no
    // #search_results to snapshot — capture just the wrapper + query there.
    this.snapshot = {
      wrapperHTML: this.wrapperTarget.innerHTML,
      resultsHTML: this.resultsElement ? this.resultsElement.innerHTML : null,
      query: this.queryElement ? this.queryElement.value : null,
      url: window.location.href,
      scrollY: window.scrollY,
    };
    // Live submits during the session should replace (not stack) history, so the
    // back button and cancel both land on the committed URL rather than a
    // half-explored query. search_controller#updateURL reads this flag.
    if (this.formElement) this.formElement.dataset.modalSession = 'true';

    // A close mid-animation leaves a pending re-hide timer; cancel it so a quick
    // reopen doesn't get yanked back to display:none underneath the slide-in.
    if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }

    // Mobile Safari only opens its software keyboard when focus happens inside
    // the original tap. Focus an empty query against the panel's stable layout
    // box while that preparation frame is clipped, then start the translated
    // entrance on the following frame. This is the same pattern as the mobile
    // profile text editor: autofocus without letting focus cancel the slide-in.
    const query = this.queryElement;
    // Read the rendered presentation rather than viewport dimensions: the same
    // wrapper is display:none while parked as a mobile drawer, but md:block as
    // the persistent desktop sidebar.
    const opensAsDrawer = getComputedStyle(this.wrapperTarget).display === 'none';
    const shouldFocusQuery = opensAsDrawer && query && query.value.trim() === '';

    window.cancelAnimationFrame(this.entranceFrame);
    this.wrapperTarget.classList.toggle('search-modal--focus-preparing', shouldFocusQuery);
    this.wrapperTarget.classList.remove('hidden');

    if (shouldFocusQuery) {
      void this.wrapperTarget.offsetWidth;
      query.focus({ preventScroll: true });

      this.entranceFrame = window.requestAnimationFrame(() => {
        this.wrapperTarget.classList.remove('search-modal--focus-preparing');
        this.wrapperTarget.classList.add('is-open', 'search-modal--focus-entrance');
        // Some browsers return focus to the tapped opener after its click
        // finishes. The synchronous focus above is what authorises the iOS
        // keyboard; this second call keeps the query active once the entrance
        // frame has started.
        query.focus({ preventScroll: true });
        this.entranceFrame = null;
      });
    } else {
      void this.wrapperTarget.offsetWidth;
      this.wrapperTarget.classList.add('is-open');
    }

    // Mobile defers predictive counts until the user actually opens search.
    // Desktop loads them after paint from facet_counts_controller itself.
    this.element.dispatchEvent(new CustomEvent('facets:load'));
  }

  // "Show results" — keep the live filter state and close. A query that was
  // typed but not yet submitted (Enter) goes out with this tap, so
  // closing the modal doesn't leave the box and the results disagreeing.
  // Cancel passes { skipSearch: true } after restoring the snapshot; that
  // restore must stay local, with no request.
  close(event) {
    const form = this.formElement;
    const submitting = !event?.skipSearch && this.searchController?.queryIsDirty();
    if (submitting) {
      // Leave modalSession set until this request finishes so updateURL still
      // replaces history, the same as Enter while the modal is open.
      form?.addEventListener('turbo:submit-end', () => {
        delete form.dataset.modalSession;
      }, { once: true });
      this.searchController.submitQuery();
    } else if (form && !event?.preserveSession) {
      delete form.dataset.modalSession;
    }
    // Slide back out, then restore display:none once the panel is off-screen so
    // the parked wrapper can't add horizontal overflow. The delay matches the
    // 150ms CSS transition; the guard in openModal clears it on a fast reopen.
    window.cancelAnimationFrame(this.entranceFrame);
    this.entranceFrame = null;
    this.wrapperTarget.classList.remove(
      'is-open',
      'search-modal--focus-preparing',
      'search-modal--focus-entrance'
    );
    // A search that already replaced the list scrolled to the top behind the
    // sheet. Scroll again once the sheet is gone: iOS can drop a scroll that
    // happened while a fixed overlay covered the page.
    const scrollToTop = !event?.skipSearch && this.resultsChangedSinceOpen();
    this.closeTimer = setTimeout(() => {
      this.wrapperTarget.classList.add('hidden');
      if (scrollToTop) window.scrollTo(0, 0);
      this.closeTimer = null;
    }, 150);
  }

  // Enter on mobile is an explicit request to see the new result set, not an
  // invitation to wait inside the filter sheet. Submit once, slide the sheet
  // away immediately, and leave the old cards visible in their pending state.
  submitQueryAndClose() {
    const form = this.formElement;
    if (!form || !this.searchController) return;

    form.addEventListener('turbo:submit-end', () => {
      delete form.dataset.modalSession;
      window.scrollTo(0, 0);
    }, { once: true });

    this.searchController.submitQuery();
    this.close({ skipSearch: true, preserveSession: true });
  }

  // "Show results" in the cold modal — there's nothing to show in place, so
  // navigate to the results page carrying the filters the form has collected.
  showResults(e) {
    e?.preventDefault();
    const form = this.formElement;
    if (!form) return;

    // The CTA can be tapped without pressing Enter first. Commit the query and
    // relevance ordering locally, but do not submit the embedded/count-only
    // request — the following Turbo visit is the one and only search request.
    if (this.searchController?.queryIsDirty()) {
      this.searchController.prepareQuerySubmit();
    }

    const params = new URLSearchParams(new FormData(form));
    params.delete('embedded'); // the live-count-only marker; not a filter
    const action = form.getAttribute('action') || '/videos';
    const url = `${action}?${params.toString()}`;

    this.beginColdNavigation();
    if (window.Turbo) {
      window.Turbo.visit(url);
    } else {
      window.location.assign(url);
    }
  }

  beginColdNavigation() {
    const query = this.queryElement?.value.trim();
    if (this.hasLoadingQueryTarget) {
      this.loadingQueryTarget.textContent = query || 'All lessons';
    }
    this.wrapperTarget.classList.add('is-searching');
    this.wrapperTarget.setAttribute('aria-busy', 'true');
  }

  endColdNavigation() {
    this.wrapperTarget.classList.remove('is-searching');
    this.wrapperTarget.removeAttribute('aria-busy');
  }

  // Turbo snapshots the departing page after the loading shell is shown. Never
  // cache that transient state, or Back would restore an already-busy modal.
  prepareForCache() {
    this.endColdNavigation();
    window.cancelAnimationFrame(this.entranceFrame);
    this.entranceFrame = null;
    this.wrapperTarget.classList.remove(
      'is-open',
      'search-modal--focus-preparing',
      'search-modal--focus-entrance'
    );
    this.wrapperTarget.classList.add('hidden');
  }

  // "Cancel" — restore the committed state captured on open, instantly.
  cancel() {
    if (this.snapshot) {
      this.wrapperTarget.innerHTML = this.snapshot.wrapperHTML;
      if (this.resultsElement && this.snapshot.resultsHTML !== null) {
        this.resultsElement.innerHTML = this.snapshot.resultsHTML;
      }
      // The query field lives in the wrapper but isn't re-rendered by submits,
      // so its value can drift from the restored markup — set it explicitly.
      if (this.queryElement) this.queryElement.value = this.snapshot.query;
      // The restored box is the committed query again. Otherwise a later
      // Show results tap treats the restored text as uncommitted and searches.
      this.searchController?.rememberQuery();
      window.history.replaceState(window.history.state, null, this.snapshot.url);
      window.rafaRememberVisit?.();
      // The in-modal search snapped the page to the top. Put the list back
      // where it was, since the results themselves were just restored.
      window.scrollTo(0, this.snapshot.scrollY || 0);
    }
    this.close({ skipSearch: true });
  }

  get searchController() {
    const form = this.formElement;
    if (!form) return null;

    return this.application.getControllerForElementAndIdentifier(form, 'search');
  }

  get formElement() {
    return this.element.querySelector('form');
  }

  get resultsElement() {
    return document.getElementById('search_results');
  }

  // Desktop renders the same wrapper as a persistent sidebar. `is-open` is set
  // only when the mobile sheet is actively covering the page, making it a more
  // reliable signal than viewport APIs in resized/webview environments.
  get isOpen() {
    return this.wrapperTarget.classList.contains('is-open');
  }

  get queryElement() {
    return this.element.querySelector('[name="query"]');
  }

  resultsChangedSinceOpen() {
    if (!this.snapshot || this.snapshot.resultsHTML === null || !this.resultsElement) return false;

    return this.resultsElement.innerHTML !== this.snapshot.resultsHTML;
  }
}
